import { action, computed, IReactionDisposer, makeObservable, observable, when } from 'mobx';

import { LaunchType, MaintenancePhase } from '../enums';
import { MaintenanceNotify } from '../models';
import { launcherSocket } from '../api';
import { RootStore } from '.';

/**
 * @description planned maintenance pushed over the launcher socket (`MaintenanceNotify`): a banner counts
 * down to `startDate`, then a running round is allowed to finish, then the maintenance page replaces the
 * game. Kept apart from `launchData.launchType`: setting that would re-run `GameLaunch`'s effect, which
 * re-inits the integration and regenerates the game URL.
 */
export class MaintenanceStore {

  private rootStore: RootStore;

  /**
   * @description the latest push; a newer one during the countdown replaces it
   */
  public notice: MaintenanceNotify | null = null;

  public phase = MaintenancePhase.Idle;

  /**
   * @description epoch ms the banner counts down to: the receipt time plus what is left until `startDate`
   */
  public deadline = 0;

  private countdownTimer: NodeJS.Timeout | null = null;

  // holds back a launch-data maintenance until `notifySeconds` before its start
  private launchNoticeTimer: NodeJS.Timeout | null = null;

  private roundWaitDisposer: IReactionDisposer | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      notice: observable,
      setNotice: action.bound,

      phase: observable,
      setPhase: action.bound,

      deadline: observable,
      setDeadline: action.bound,

      endTime: computed,
      showBanner: computed,
      isAwaitingRound: computed,
      showMaintenancePage: computed,
      isMaintenanceMode: computed
    });

    // Hidden tabs run timers as rarely as once a minute, so the deadline can pass unnoticed until the player is back.
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
  }

  public get endTime(): number {
    return this.notice ? new Date(this.notice.endDate).getTime() : 0;
  }

  public get showBanner(): boolean {
    return this.phase === MaintenancePhase.Countdown || this.phase === MaintenancePhase.AwaitingRound;
  }

  public get isAwaitingRound(): boolean {
    return this.phase === MaintenancePhase.AwaitingRound;
  }

  public get showMaintenancePage(): boolean {
    return this.phase === MaintenancePhase.Active;
  }

  /**
   * @description the maintenance page replaces the game: opened by the launch data, or by a push that ran its course
   */
  public get isMaintenanceMode(): boolean {
    return this.showMaintenancePage || this.rootStore.gameStore.launchData.data?.launchType === LaunchType.Maintenance;
  }

  public setNotice(value: MaintenanceNotify | null): void {
    this.notice = value;
  }

  public setPhase(value: MaintenancePhase): void {
    this.phase = value;
  }

  public setDeadline(value: number): void {
    this.deadline = value;
  }

  public listenEvents(): void {
    launcherSocket.onMaintenanceNotify(this.handleMaintenanceNotify.bind(this));
  }

  /**
   * @description `maintenanceInfo` of the launch data: a launch into an already scheduled maintenance starts
   * the same countdown as the push. Launch data can carry it long before the push would come, so the banner
   * is held back until `notifySeconds` before the start. The push normally lands at that same moment and
   * cancels the hold; the timer only covers a hub that is down.
   */
  public handleLaunchMaintenance(info: MaintenanceNotify): void {
    this.clearLaunchNoticeTimer();
    const untilNotice = new Date(info.startDate).getTime() - (info.notifySeconds || 0) * 1000 - Date.now();
    if (untilNotice > 0) {
      this.launchNoticeTimer = setTimeout(() => this.handleMaintenanceNotify(info), untilNotice);
      return;
    }
    this.handleMaintenanceNotify(info);
  }

  public handleMaintenanceNotify(data: MaintenanceNotify): void {
    // The push supersedes a launch-data notice still held back. A push after that notice fired only
    // restarts the countdown on the same `startDate`, so the deadline does not move.
    this.clearLaunchNoticeTimer();

    // Past 00:00 the session is already winding down; a repeated push must not restart the countdown.
    if (this.phase === MaintenancePhase.AwaitingRound || this.phase === MaintenancePhase.Active) return;

    // The launch-time maintenance page is already up, or this launcher is nested in a replay frame
    // whose parent shows the banner itself.
    const launchType = this.rootStore.gameStore.launchData.data?.launchType;
    if (launchType === LaunchType.Maintenance) return;

    this.clearCountdownTimer();

    const notifyMs = Math.max(0, new Date(data.startDate).getTime() - Date.now());
    this.setNotice(data);
    this.setDeadline(Date.now() + notifyMs);
    this.setPhase(MaintenancePhase.Countdown);

    if (!notifyMs) {
      this.finishCountdown();
      return;
    }
    this.countdownTimer = setTimeout(this.finishCountdown.bind(this), notifyMs);
  }

  private handleVisibilityChange(): void {
    if (document.hidden || this.phase !== MaintenancePhase.Countdown) return;
    if (Date.now() >= this.deadline) {
      this.finishCountdown();
    }
  }

  private finishCountdown(): void {
    if (this.phase !== MaintenancePhase.Countdown) return;
    this.clearCountdownTimer();

    const { gameStore, postMessageStore } = this.rootStore;
    if (!gameStore.roundInProgress) {
      this.activate();
      return;
    }

    // Let the running round settle, but keep autoplay from starting the next one.
    this.setPhase(MaintenancePhase.AwaitingRound);
    postMessageStore.sendStopAutoSpins();
    this.roundWaitDisposer = when(
      () => !this.rootStore.gameStore.roundInProgress,
      () => this.activate()
    );
  }

  private activate(): void {
    this.clearCountdownTimer();
    this.roundWaitDisposer?.();
    this.roundWaitDisposer = null;
    this.setPhase(MaintenancePhase.Active);
  }

  private clearLaunchNoticeTimer(): void {
    if (this.launchNoticeTimer) {
      clearTimeout(this.launchNoticeTimer);
      this.launchNoticeTimer = null;
    }
  }

  private clearCountdownTimer(): void {
    if (this.countdownTimer) {
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
  }
}
